import cn from 'classnames'
import { Percent } from 'legacy/lut/components/widgets/public/partner360/common/reports/reportsCommon/percent/Percent'
import {
  getFormattedPercent,
  getFormattedRevenue,
} from 'legacy/lut/components/widgets/public/partner360/helpers/report'
import { FC, useMemo } from 'react'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Tooltip } from 'core-components/library/tooltip'
import { IQuarterlyMarketTableRow } from 'partner360/models/report/sobAnalyticsReports/iQuarterlyMarketTableRow'

import { TRANSLATION_KEYS } from '../../../translations'
import { getCurrYearBaseFields, getFieldWithSellType } from '../../helpers/fieldsNameHelper'
import styles from '../../QuarterlyViewMarketTable.module.scss'

interface IQuarterlyViewMarketFooterProps {
  totalData: IQuarterlyMarketTableRow
  sellType: string
  isFullYear: boolean
  // group 2 and 3 current-year cells show Expected Exit
  isExpectedExit?: boolean
}

export const QuarterlyViewMarketFooter: FC<IQuarterlyViewMarketFooterProps> = ({
  totalData,
  sellType,
  isFullYear,
  isExpectedExit = false,
}) => {
  const baseFields = getCurrYearBaseFields(isExpectedExit)

  const prevYearActualsRevenueTotalField = getFieldWithSellType('prevYearActualsRevenue', sellType)
  const prevYearActualsRevenueTotal = totalData[prevYearActualsRevenueTotalField]

  const currYearActualsRevenueTotalField = getFieldWithSellType(baseFields.revenue, sellType)
  const currYearActualsRevenueTotal = totalData[currYearActualsRevenueTotalField]
  const hasNoPrevYearActualsRevenue = !prevYearActualsRevenueTotal
  const hasNoPrevYearSobField = getFieldWithSellType('prevYearSobActual', sellType)
  const hasNoPrevYearSob = !totalData[hasNoPrevYearSobField]

  const hasNegativePrevYearActualsRevenue = prevYearActualsRevenueTotal
    ? prevYearActualsRevenueTotal < 0
    : false

  const hasNoCurrYearActualsRevenue = !currYearActualsRevenueTotal
  const hasNoCurrYearSobField = getFieldWithSellType(baseFields.sob, sellType)
  const hasNoCurrYearSob = !totalData[hasNoCurrYearSobField]
  const hasNegativeCurrYearActualsRevenue = currYearActualsRevenueTotal
    ? currYearActualsRevenueTotal < 0
    : false

  const hidePlannedColumns = useMemo(() => ['TNet', 'UTNet'].includes(sellType), [sellType])

  const yoyBaselineValue = totalData[getFieldWithSellType('yoyBaseline', sellType)]
  const prevYearYoYPPValue = totalData[getFieldWithSellType(baseFields.yoyPP, sellType)]
  const currYearQoQValue = totalData[getFieldWithSellType(baseFields.qoq, sellType)]
  const prevYearVsPlanValue = totalData[getFieldWithSellType(baseFields.vsPlan, sellType)]
  const currYearActualsRevenueValue = totalData[getFieldWithSellType(baseFields.revenue, sellType)]
  const currYearSalesPerformanceValue =
    totalData[getFieldWithSellType(baseFields.salesPerformance, sellType)]
  const currYearYoYGrowthValue = totalData[getFieldWithSellType(baseFields.yoyGrowth, sellType)]
  const yoySales = totalData[getFieldWithSellType('yoyRevenue', sellType)]

  return (
    <tfoot>
      <tr>
        <td />
        <td className={cn(styles.commercialHWColumn, styles.borderLeft)}>{totalData.title}</td>

        <td className={cn(styles.commercialHWColumn, styles.borderLeft, styles.numeric)}>
          {totalData.partnersCount}
        </td>

        <td className={cn(styles.commercialHWColumn, styles.borderLeft, styles.numeric)}>
          {getFormattedPercent({
            percent: totalData[getFieldWithSellType('coverageWW', sellType)],
          })}
        </td>
        <td className={cn(styles.currentYearColumn, styles.borderLeft, styles.numeric)}>
          <div
            className={cn({
              [styles.noRevenueCell]:
                hasNoPrevYearActualsRevenue ||
                hasNoPrevYearSob ||
                hasNegativePrevYearActualsRevenue,
            })}
          >
            <Tooltip
              disabled={
                !hasNoPrevYearActualsRevenue &&
                !hasNoPrevYearSob &&
                !hasNegativePrevYearActualsRevenue
              }
              tipContent={() => (
                <>
                  <RenderWhen condition={hasNoPrevYearActualsRevenue}>
                    <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNoPrevYearSob}>
                    <Translation text={TRANSLATION_KEYS.hasNoSobTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNegativePrevYearActualsRevenue}>
                    <Translation text={TRANSLATION_KEYS.negativeRevenueTooltip} />
                  </RenderWhen>
                </>
              )}
            >
              {getFormattedPercent({
                percent: totalData[getFieldWithSellType('prevYearSobActual', sellType)],
              })}
            </Tooltip>
          </div>
        </td>
        <td className={cn(styles.currentYearColumn, styles.borderLeft, styles.numeric)}>
          {getFormattedPercent({
            percent: totalData[getFieldWithSellType('currYearSobPlan', sellType)],
          })}
        </td>
        <td className={cn(styles.currentYearColumn, styles.borderLeft, styles.numeric)}>
          <span
            className={cn({
              [styles.positive]: yoyBaselineValue != null && yoyBaselineValue > 0,
              [styles.negative]: yoyBaselineValue != null && yoyBaselineValue < 0,
            })}
          >
            {getFormattedPercent({
              percent: yoyBaselineValue,
              ending: 'pp',
              hasValueSign: true,
            })}
          </span>
        </td>
        <td className={cn(styles.currentYearColumn, styles.borderLeft, styles.numeric)}>
          <div
            className={cn({
              [styles.noRevenueCell]:
                hasNoCurrYearSob ||
                hasNoCurrYearActualsRevenue ||
                hasNegativeCurrYearActualsRevenue,
            })}
          >
            <Tooltip
              disabled={
                !hasNoCurrYearSob &&
                !hasNoCurrYearActualsRevenue &&
                !hasNegativeCurrYearActualsRevenue
              }
              tipContent={() => (
                <>
                  <RenderWhen condition={hasNoCurrYearActualsRevenue}>
                    <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNoCurrYearSob}>
                    <Translation text={TRANSLATION_KEYS.hasNoSobTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNegativeCurrYearActualsRevenue}>
                    <Translation text={TRANSLATION_KEYS.negativeRevenueTooltip} />
                  </RenderWhen>
                </>
              )}
            >
              {getFormattedPercent({
                percent: totalData[getFieldWithSellType(baseFields.sob, sellType)],
              })}
            </Tooltip>
          </div>
        </td>
        <td className={cn(styles.currentYearColumn, styles.borderLeft, styles.numeric)}>
          <div
            className={cn({
              [styles.noRevenueCell]:
                hasNoCurrYearActualsRevenue ||
                hasNoPrevYearActualsRevenue ||
                hasNegativePrevYearActualsRevenue ||
                hasNegativeCurrYearActualsRevenue,
            })}
          >
            <Tooltip
              disabled={
                !hasNoCurrYearActualsRevenue &&
                !hasNoPrevYearActualsRevenue &&
                !hasNegativePrevYearActualsRevenue &&
                !hasNegativeCurrYearActualsRevenue
              }
              tipContent={() => (
                <>
                  <RenderWhen
                    condition={hasNoCurrYearActualsRevenue || hasNoPrevYearActualsRevenue}
                  >
                    <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />
                  </RenderWhen>
                  <RenderWhen
                    condition={
                      hasNegativePrevYearActualsRevenue || hasNegativeCurrYearActualsRevenue
                    }
                  >
                    <Translation text={TRANSLATION_KEYS.negativeRevenueTooltip} />
                  </RenderWhen>
                </>
              )}
            >
              <span
                className={cn({
                  [styles.positive]: prevYearYoYPPValue != null && prevYearYoYPPValue > 0,
                  [styles.negative]: prevYearYoYPPValue != null && prevYearYoYPPValue < 0,
                })}
              >
                {getFormattedPercent({
                  percent: prevYearYoYPPValue,
                  ending: 'pp',
                  hasValueSign: true,
                })}
              </span>
            </Tooltip>
          </div>
        </td>
        <RenderWhen condition={!isFullYear}>
          <td className={cn(styles.currentYearColumn, styles.borderLeft, styles.numeric)}>
            <div
              className={cn({
                [styles.noRevenueCell]:
                  hasNoCurrYearActualsRevenue ||
                  hasNoPrevYearActualsRevenue ||
                  hasNegativeCurrYearActualsRevenue ||
                  hasNegativePrevYearActualsRevenue ||
                  hasNoCurrYearSob ||
                  hasNoPrevYearSob,
              })}
            >
              <Tooltip
                disabled={
                  !hasNoCurrYearActualsRevenue &&
                  !hasNoPrevYearActualsRevenue &&
                  !hasNegativeCurrYearActualsRevenue &&
                  !hasNegativePrevYearActualsRevenue &&
                  !hasNoCurrYearSob &&
                  !hasNoPrevYearSob
                }
                tipContent={() => (
                  <>
                    <RenderWhen
                      condition={hasNoCurrYearActualsRevenue || hasNoPrevYearActualsRevenue}
                    >
                      <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />
                    </RenderWhen>
                    <RenderWhen condition={hasNoCurrYearSob || hasNoPrevYearSob}>
                      <Translation text={TRANSLATION_KEYS.hasNoSobTooltip} />
                    </RenderWhen>
                    <RenderWhen
                      condition={
                        hasNegativeCurrYearActualsRevenue || hasNegativePrevYearActualsRevenue
                      }
                    >
                      <Translation text={TRANSLATION_KEYS.negativeRevenueTooltip} />
                    </RenderWhen>
                  </>
                )}
              >
                <span
                  className={cn({
                    [styles.positive]: currYearQoQValue != null && currYearQoQValue > 0,
                    [styles.negative]: currYearQoQValue != null && currYearQoQValue < 0,
                  })}
                >
                  {getFormattedPercent({
                    percent: currYearQoQValue,
                    ending: 'pp',
                    hasValueSign: true,
                  })}
                </span>
              </Tooltip>
            </div>
          </td>
        </RenderWhen>
        <td className={cn(styles.currentYearColumn, styles.borderLeft, styles.numeric)}>
          <div
            className={cn({
              [styles.noRevenueCell]:
                hasNoCurrYearActualsRevenue ||
                hasNoCurrYearSob ||
                hasNegativeCurrYearActualsRevenue,
            })}
          >
            <Tooltip
              disabled={
                !hasNoCurrYearActualsRevenue &&
                !hasNoCurrYearSob &&
                !hasNegativeCurrYearActualsRevenue
              }
              tipContent={() => (
                <>
                  <RenderWhen condition={hasNoCurrYearActualsRevenue}>
                    <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNoCurrYearSob}>
                    <Translation text={TRANSLATION_KEYS.hasNoSobTooltip} />
                  </RenderWhen>
                  <RenderWhen condition={hasNegativeCurrYearActualsRevenue}>
                    <Translation text={TRANSLATION_KEYS.negativeRevenueTooltip} />
                  </RenderWhen>
                </>
              )}
            >
              <span
                className={cn({
                  [styles.positive]: prevYearVsPlanValue != null && prevYearVsPlanValue > 0,
                  [styles.negative]: prevYearVsPlanValue != null && prevYearVsPlanValue < 0,
                })}
              >
                {getFormattedPercent({
                  percent: prevYearVsPlanValue,
                  ending: 'pp',
                  hasValueSign: true,
                })}
              </span>
            </Tooltip>
          </div>
        </td>
        <td className={cn(styles.resultColumn, styles.borderLeft, styles.numeric)}>
          <Tooltip
            disabled={
              sellType === 'Net'
                ? !totalData.prevYearActualsRevenueNet
                : !totalData.prevYearActualsRevenue
            }
            tipContent={() => (
              <>
                {getFormattedRevenue(
                  totalData[getFieldWithSellType('prevYearActualsRevenue', sellType)],
                  true
                )}
              </>
            )}
          >
            {getFormattedRevenue(
              totalData[getFieldWithSellType('prevYearActualsRevenue', sellType)]
            )}
          </Tooltip>
        </td>
        {hidePlannedColumns ? null : (
          <td className={cn(styles.resultColumn, styles.borderLeft, styles.numeric)}>
            <Tooltip
              disabled={!totalData[getFieldWithSellType('currYearPlansRevenue', sellType)]}
              tipContent={() => (
                <>
                  {getFormattedRevenue(
                    totalData[getFieldWithSellType('currYearPlansRevenue', sellType)],
                    true
                  )}
                </>
              )}
            >
              {getFormattedRevenue(
                totalData[getFieldWithSellType('currYearPlansRevenue', sellType)]
              )}
            </Tooltip>
          </td>
        )}

        {hidePlannedColumns ? null : (
          <td
            className={cn(styles.resultColumn, styles.borderLeft, styles.numeric, {
              [styles.positive]: yoySales != null && yoySales > 0,
              [styles.negative]: yoySales != null && yoySales < 0,
            })}
          >
            {getFormattedPercent({
              percent: yoySales,
            })}
          </td>
        )}

        <td className={cn(styles.resultColumn, styles.borderLeft, styles.numeric)}>
          <Tooltip
            disabled={!currYearActualsRevenueValue}
            tipContent={() => <>{getFormattedRevenue(currYearActualsRevenueValue, true)}</>}
          >
            <span
              className={cn({
                [styles.positive]:
                  currYearActualsRevenueValue != null && currYearActualsRevenueValue > 0,
                [styles.negative]:
                  currYearActualsRevenueValue != null && currYearActualsRevenueValue < 0,
              })}
            >
              {getFormattedRevenue(currYearActualsRevenueValue)}
            </span>
          </Tooltip>
        </td>
        {hidePlannedColumns ? null : (
          <td className={cn(styles.resultColumn, styles.borderLeft, styles.numeric)}>
            <span>
              <Percent
                withMarker
                shouldConvertValue={false}
                value={currYearSalesPerformanceValue}
              />
            </span>
          </td>
        )}

        <td className={cn(styles.resultColumn, styles.borderLeft, styles.numeric)}>
          <div
            className={cn({
              [styles.noRevenueCell]: hasNoCurrYearActualsRevenue || hasNoPrevYearActualsRevenue,
            })}
          >
            <Tooltip
              disabled={!hasNoCurrYearActualsRevenue && !hasNoPrevYearActualsRevenue}
              tipContent={() => <Translation text={TRANSLATION_KEYS.hasNoRevenueTooltip} />}
            >
              <span
                className={cn({
                  [styles.positive]: currYearYoYGrowthValue != null && currYearYoYGrowthValue > 0,
                  [styles.negative]: currYearYoYGrowthValue != null && currYearYoYGrowthValue < 0,
                })}
              >
                {getFormattedPercent({
                  percent: currYearYoYGrowthValue,
                  hasValueSign: true,
                })}
              </span>
            </Tooltip>
          </div>
        </td>
        <td className={cn(styles.resultColumn, styles.borderLeft, styles.numeric)}>
          {getFormattedRevenue(
            totalData[getFieldWithSellType('prevYearQuarterTamActuals', sellType)]
          )}
        </td>
        {hidePlannedColumns ? null : (
          <td className={cn(styles.resultColumn, styles.borderLeft, styles.numeric)}>
            {getFormattedRevenue(
              totalData[getFieldWithSellType('currYearQuarterTamPlan', sellType)]
            )}
          </td>
        )}
        {hidePlannedColumns ? null : (
          <td className={cn(styles.resultColumn, styles.borderLeft, styles.numeric)}>
            {getFormattedPercent({
              percent: totalData[getFieldWithSellType('partnerTamYoY', sellType)],
            })}
          </td>
        )}

        <td className={cn(styles.resultColumn, styles.borderLeft, styles.numeric)}>
          {getFormattedRevenue(totalData[getFieldWithSellType(baseFields.tam, sellType)])}
        </td>
        <td className={cn(styles.resultColumn, styles.borderLeft, styles.numeric)}>
          {getFormattedPercent({
            percent: totalData[getFieldWithSellType(baseFields.tamYoY, sellType)],
          })}
        </td>
      </tr>
    </tfoot>
  )
}
